// MACHINERY V2 (Stroje) — Fleet V2 row on the PHONE (M2.16b).
//
// Rendered truth: mobile/screenshots/09-v2-phone.png, viewed before this file
// was written. Reference impl: mobile/machinery-mobile.jsx MbV2Row (:261-277);
// values verbatim — 48x36 thumb, name 13.5/600, mono sub 9.5, `minHeight: 60`,
// radius 10, padding 9/12, gap 10.
//
// >>> PHONE ONLY, AND THAT IS THE VIEW'S OWN RULE, NOT THIS FILE'S. <<<
// mobile/README.md §V2: "phone only. Tablet uses V1 grid — a middle density is
// pointless above 390." FleetContent redirects V2 to the V1 grid between 768 and
// 1279, so the only widths that reach this component are < 768. It is still
// selected on `isDesktop` rather than `isPhone` in FleetGroupSection, so if that
// redirect is ever relaxed this row degrades to rendering at tablet instead of
// vanishing.
//
// WHAT THE DESKTOP ROW HAS THAT THIS DOES NOT, and why:
//   · the 6-column grid (photo | identity | location+S/N | live job | SR chip |
//     kebab) — 120px of photo column alone is a third of a 390px screen;
//   · the SR chip and the kebab. The board draws neither, and both need width
//     this row does not have (a legal kebab is 44px below 1280 — see
//     FleetCardMenu's `touch` note — which is 11% of the viewport for a control
//     the row next to it already carries). NOTHING IS UNREACHABLE: V1 is the
//     DEFAULT view and its compact card carries the kebab and the health dot at
//     this width, V3's phone list carries the open-SR pill, and every action is
//     on the machine profile the row taps through to. That is the M2.16a
//     "re-home, never remove" rule applied across four views rather than
//     four times inside one.
//
// THE STATUS-LESS CELL IS A DASH, NOT THE LABEL. The desktop row prints
// "BEZ PRIPOJENIA" (FleetRow.tsx:136-143, re-read while writing this line); at
// 390px that label is wider than the machine name beside it, so the board prints
// a mono em-dash and this row does the same, with the state's name moved onto
// the `title`. Still NEUTRAL — no status colour on an unconnected machine, ever
// (§4.3, locked).
//
// >>> THE DASH COVERS TWO STATES, NOT ONE (review finding F7). <<< This row used
// to gate on `showStatusRow` alone, which is TRUE for a status-less `stale`
// machine ("TelemetryStale" -> `{ conn: "stale", status: null }`,
// fleetTelemetry.ts:100) — so it rendered a StatusBadge that returned null
// (StatusBadge.tsx:72) and the row showed a bare 7px dot and no chip, while V3's
// phone row printed a mono mark for the SAME machine on the SAME phone. Both
// surfaces now ask ONE predicate, `showsStatusChip` (fleetActivity.ts), and the
// tooltip names the actual state instead of calling every dash "unconnected".
//
// SINGLE DERIVATION. deriveCardPresentation() decides the badge, the dot and the
// dash; nothing here re-tests `alarms.length`, `status` or `conn`.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { useGalleryImages } from "../../../../shared/components/gallery";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import ConnDot from "./ConnDot";
import StatusBadge, { AwaitingBadge } from "./StatusBadge";
import { MachinePhoto, NewBadge } from "./fleetCardAtoms";
import { MachineryV2Telemetry, deriveCardPresentation } from "./cardStates";
import { cardStateLabelKey, showsStatusChip } from "./fleetActivity";

export interface FleetRowCompactProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  isNew: boolean;
  onOpenProfile: () => void;
}

const FleetRowCompact: React.FC<FleetRowCompactProps> = ({
  machine, telemetry, isNew, onOpenProfile,
}) => {
  const { translate } = useTranslation("myMachinery");
  const p = deriveCardPresentation(telemetry);
  const decommissioned = !!machine.isDecommissioned;

  const images = useGalleryImages({
    type: "machine",
    image: machine.image?.content || null,
    catalogImageUrl:
      machine.catalogModel?.imageStandardUrl || machine.catalogModel?.imageUrl || null,
    catalogImages: machine.catalogModel?.images || [],
    files: machine.files?.map((f) => ({ name: f.name, content: f.content })) || [],
  });

  // The board's `Hala A · S/N 34234`: the DEEPEST location name (a phone has no
  // room for "Závod › Hala"), then the serial. Empty segments are dropped
  // rather than printed as placeholders.
  const sub = [machine.locationName, machine.serialNumber ? "S/N " + machine.serialNumber : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <article
      onClick={onOpenProfile}
      data-machine-id={machine.id}
      style={{
        display: "flex", alignItems: "center", gap: 10, padding: "9px 12px",
        background: "var(--card)", borderRadius: 10, minHeight: 60,
        border: decommissioned ? "1px dashed var(--border)" : "1px solid var(--border)",
        opacity: decommissioned ? 0.65 : 1,
        minWidth: 0, cursor: "pointer",
      }}
    >
      <MachinePhoto
        src={images.length > 0 ? images[0].src : null}
        alt={getMachineDisplayName(machine)}
        grayscale={decommissioned}
        width={48}
        height={36}
      />

      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span
            style={{
              fontSize: 13.5, fontWeight: 600, color: "var(--ink)", minWidth: 0,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {getMachineDisplayName(machine)}
          </span>
          {isNew && <NewBadge />}
        </div>
        {sub && (
          <div
            style={{
              fontFamily: "var(--font-mono)", fontSize: 9.5, color: "var(--faint)",
              marginTop: 1, whiteSpace: "nowrap", overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {sub}
          </div>
        )}
      </div>

      {showsStatusChip(p) ? (
        p.showAwaitingBadge ? (
          <AwaitingBadge />
        ) : (
          <StatusBadge status={p.badgeStatus} stale={p.staleLook} variant="chip" />
        )
      ) : (
        <span
          title={translate(cardStateLabelKey(p.state))}
          style={{
            fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--faint)",
            flexShrink: 0,
          }}
        >
          —
        </span>
      )}
      {/* OUTSIDE the branch: the connectivity dot is a SEPARATE signal (§4.3),
          and a `stale` machine that now shows the dash still has an amber one to
          show. It renders nothing on its own for unconnected / awaiting —
          `dotConn` is null there (ConnDot.tsx:64) — so the previously-nested
          placement was never what suppressed it. */}
      <ConnDot state={p.dotConn} size={7} lastHeartbeat={telemetry.heartbeat} />
    </article>
  );
};

export default FleetRowCompact;
